import {
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faRotate,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { JoonUIColor, FlexBox, SPACING, Typography } from "@joonapp/web-shared"
import { User } from "@sendbird/chat"
import { BaseMessage } from "@sendbird/chat/message"
import { captureException } from "@sentry/react"
import { useEffect } from "react"

import { useSendbird } from "./SBProvider"
import useAIMessageStore, { AISuggestion } from "./useAIMessageStore"
import { useSBStore } from "./useSBStore"
import useCurrentClient from "../../pages/clients/hooks/useCurrentClient"
import { Gradient } from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { createJoonAPIClient } from "../../util/joon-api"
import AnimatedStars from "../animatedStars/AnimatedStars"
import IconButton from "../button/IconButton"

const AIMessageSuggestionBox = () => {
  const {
    isLoading,
    suggestions,
    setSuggestions,
    currentSuggestionIndex,
    setCurrentSuggestionIndex,
    setHasCopiedSuggestion,
  } = useAIMessageStore()
  const { setInputText } = useSBStore()
  const { currentChannel } = useSendbird()

  useEffect(() => {
    setSuggestions([])
  }, [currentChannel, setSuggestions])

  if (!suggestions.length && !isLoading) return null

  const handleCopySuggestionToInput = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault()
    setInputText(suggestions[currentSuggestionIndex].content)
    setCurrentSuggestionIndex(0)
    setSuggestions([])
    trackAnalyticEvent(ANALYTIC_EVENTS.AI_PICK)
    setHasCopiedSuggestion(true)
  }

  const currentSuggestion = suggestions[currentSuggestionIndex]

  return (
    <FlexBox
      direction="row"
      align="center"
      wrap={false}
      gap={SPACING.space2}
      style={{
        background: Gradient.g100,
        padding: SPACING.space4,
        borderBottom: `1px solid ${JoonUIColor.border.default}`,
      }}
    >
      {isLoading && <AnimatedStars animate={isLoading} />}
      <Typography variant="bodySmall" color={JoonUIColor.text.primary}>
        {isLoading
          ? "Generating..."
          : currentSuggestion?.content.split("\n").map((line, index) => (
              <span key={index}>
                {line}
                <br />
              </span>
            ))}
      </Typography>
      {!isLoading && currentSuggestion && (
        <IconButton
          icon={
            <FontAwesomeIcon
              icon={faArrowDown}
              color={JoonUIColor.icon.neutral}
              style={{ margin: `0 ${SPACING.space2}` }}
            />
          }
          onClick={handleCopySuggestionToInput}
          tooltip="Insert suggestion"
        />
      )}
    </FlexBox>
  )
}

const AIMessageActionBar = () => {
  const {
    isLoading,
    setIsLoading,
    setSuggestions,
    suggestions,
    currentSuggestionIndex,
    setCurrentSuggestionIndex,
    hasError,
    setHasError,
  } = useAIMessageStore()
  const { generateAISuggestions } = useGenerateAISuggestions()

  const handleGenerateMessage = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault()
    setIsLoading(true)
    setHasError(false)
    trackAnalyticEvent(ANALYTIC_EVENTS.AI_GENERATE)
    try {
      const results = await generateAISuggestions()
      if (results) setSuggestions(results)
      else setHasError(true)
    } catch (error) {
      setHasError(true)
      captureException(error)
      console.error(error)
      trackAnalyticEvent(ANALYTIC_EVENTS.AI_ERROR)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseSuggestions = () => {
    setSuggestions([])
    setCurrentSuggestionIndex(0)
    trackAnalyticEvent(ANALYTIC_EVENTS.AI_CLOSE)
  }

  const goToPreviousSuggestion = () => {
    const previousIndex = currentSuggestionIndex - 1
    const newIndex = previousIndex < 0 ? suggestions.length - 1 : previousIndex
    setCurrentSuggestionIndex(newIndex)
    trackAnalyticEvent(ANALYTIC_EVENTS.AI_PREV)
  }

  const goToNextSuggestion = () => {
    const nextIndex = currentSuggestionIndex + 1
    const newIndex = nextIndex >= suggestions.length ? 0 : nextIndex
    setCurrentSuggestionIndex(newIndex)
    trackAnalyticEvent(ANALYTIC_EVENTS.AI_NEXT)
  }

  return (
    <FlexBox
      direction="row"
      align="center"
      justify="space-between"
      wrap={false}
      gap={SPACING.space1}
      style={{ padding: `${SPACING.space1} ${SPACING.space0}` }}
    >
      {suggestions.length > 0 && (
        <>
          <FlexBox
            direction="row"
            align="center"
            wrap={false}
            gap={SPACING.space1}
          >
            <IconButton
              icon={
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  color={JoonUIColor.text.secondary}
                />
              }
              onClick={goToPreviousSuggestion}
              tooltip="Previous suggestion"
            />
            <IconButton
              icon={
                <FontAwesomeIcon
                  icon={faArrowRight}
                  color={JoonUIColor.text.secondary}
                />
              }
              onClick={goToNextSuggestion}
              tooltip="Next suggestion"
            />
            <Typography variant="bodySmall" color={JoonUIColor.text.primary}>
              {suggestions[currentSuggestionIndex].title}
            </Typography>
          </FlexBox>
          <IconButton
            icon={
              <FontAwesomeIcon
                icon={faRotate}
                color={JoonUIColor.text.secondary}
              />
            }
            onClick={handleGenerateMessage}
            tooltip="Re-generate"
            isFunButton
          />
          <IconButton
            icon={
              <FontAwesomeIcon
                icon={faXmark}
                color={JoonUIColor.text.secondary}
              />
            }
            onClick={handleCloseSuggestions}
            tooltip="Close"
          />
        </>
      )}
      {!isLoading && !suggestions.length && (
        <IconButton
          icon={
            <FontAwesomeIcon
              icon={faRotate}
              color={JoonUIColor.text.secondary}
            />
          }
          label={`${
            hasError
              ? "Error generating suggestion. Retry?"
              : "Generate suggestion"
          }`}
          onClick={handleGenerateMessage}
          isFunButton
        />
      )}
    </FlexBox>
  )
}

export { AIMessageSuggestionBox, AIMessageActionBar }

const useGenerateAISuggestions = () => {
  const { messages, currentChannel } = useSendbird()
  const { currentCoachingGroup } = useCurrentClient()

  const generateAISuggestions = async () => {
    if (!currentChannel) return
    if (!currentCoachingGroup) return

    const formattedMessages = messages
      .filter(
        // @ts-ignore
        (message: BaseMessage & { sender: User }) => message.sender?.userId
      )
      .map(
        // @ts-ignore
        (message: BaseMessage & { sender: User }) => ({
          profile_id: Number(message.sender?.userId),
          content: message.message,
          date_sent: new Date(message.createdAt).toISOString(),
        })
      )

    const API = createJoonAPIClient()

    const response = await API.post<{ suggestions: AISuggestion[] }>(
      `/api/coaching-groups/${currentCoachingGroup.id}/suggest-responses/`,
      { messages: formattedMessages }
    )

    return response.data.suggestions
  }

  return { generateAISuggestions }
}
